export type WelcomeHaniPhase = "perched" | "happy" | "ducking" | "hidden";

/** Presentation fits inside Learn's existing 500ms map transition; it never gates navigation. */
export const WELCOME_HANI_HAPPY_MS = 120;
export const WELCOME_HANI_HIDE_MS = 380;

/** Adapter for the Rive `mode` enum and HaniSheetPerch's reveal progress. No visibility policy. */
export function welcomeHaniPresentation(phase: WelcomeHaniPhase) {
  return {
    durationMs: phase === "ducking" ? WELCOME_HANI_HIDE_MS - WELCOME_HANI_HAPPY_MS : 0,
    mode: phase === "perched" ? ("idle" as const) : ("happy" as const),
    progress: phase === "perched" || phase === "happy" ? 1 : 0,
    visible: phase !== "hidden",
  };
}

/**
 * One greeting lifetime. The owner decides eligibility and retains that decision across sheet
 * remounts; this controller deliberately stores nothing on disk or across app sessions.
 * `onContinue` is the existing reward/navigation action and runs synchronously, even if artwork
 * never loads. Cancel on unmount or interruption; create a new instance only for a new greeting.
 */
export function createWelcomeHaniGreeting({
  onContinue,
  onPhase,
}: {
  onContinue: () => void;
  onPhase: (phase: WelcomeHaniPhase) => void;
}) {
  let pressed = false;
  let cancelled = false;
  let duckTimer: ReturnType<typeof setTimeout> | undefined;
  let hideTimer: ReturnType<typeof setTimeout> | undefined;
  const clearTimers = () => {
    clearTimeout(duckTimer);
    clearTimeout(hideTimer);
  };
  return {
    cancel: () => {
      cancelled = true;
      clearTimers();
    },
    continueLearning: ({
      artworkReady,
      reduceMotion,
    }: {
      artworkReady: boolean;
      reduceMotion: boolean;
    }): boolean => {
      if (pressed || cancelled) {
        return false;
      }
      pressed = true;
      // Schedule presentation before navigation, which may synchronously unmount and cancel us.
      if (artworkReady && !reduceMotion) {
        duckTimer = setTimeout(() => {
          if (!cancelled) {
            onPhase("ducking");
          }
        }, WELCOME_HANI_HAPPY_MS);
        hideTimer = setTimeout(() => {
          if (!cancelled) {
            onPhase("hidden");
          }
        }, WELCOME_HANI_HIDE_MS);
      }
      // A presentation failure must not swallow the learner's press.
      try {
        onPhase(artworkReady && !reduceMotion ? "happy" : "hidden");
      } finally {
        onContinue();
      }
      return true;
    },
  };
}
